import type {
  MealPlan,
  DayItinerary,
  PackageDeparture,
  Formula
} from '../../../backend/graphql/common';

// Définir les énumérations comme des valeurs
export enum Category {
  OMRA = "OMRA",
  ETE = "ETE",
  REVEILLON = "REVEILLON",
  SAHARA = "SAHARA",
  ALGERIE = "ALGERIE",
  EGYPT = "EGYPT",
  TUNISIE = "TUNISIE",
  TURQUIE = "TURQUIE",
  MALAISIE = "MALAISIE",
  DUBAI = "DUBAI"
}

export enum PackageType {
  B2B = 'B2B',
  B2C = 'B2C'
}

export enum PriceType {
  PER_PERSON = 'PER_PERSON',
  PER_ROOM = 'PER_ROOM'
}

export const CATEGORY_LABELS = {
  [Category.OMRA]: 'Omra',
  [Category.ETE]: 'Été',
  [Category.REVEILLON]: 'Réveillon',
  [Category.SAHARA]: 'Sahara',
  [Category.ALGERIE]: 'Algérie',
  [Category.EGYPT]: 'Égypte',
  [Category.TUNISIE]: 'Tunisie',
  [Category.TURQUIE]: 'Turquie',
  [Category.MALAISIE]: 'Malaisie',
  [Category.DUBAI]: 'Dubaï'
};

export const PACKAGE_TYPE_LABELS = {
  [PackageType.B2B]: 'B2B',
  [PackageType.B2C]: 'B2C'
};

// Exporter les types
export type {
  MealPlan,
  DayItinerary,
  PackageDeparture,
  Formula
};

// Types spécifiques au frontend...

export interface FormFields {
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: Category;
  departure: string;
  departures: PackageDeparture[];
  highlights: string[];
  included: string[];
  excluded: string[];
  cities: string[];
  countries: string[];
  formulas: Formula[];
  itinerary: DayItinerary[];
  type: PackageType;
}

export const SERVICE_OPTIONS = [
  { value: 'wifi', label: 'WiFi' },
  { value: 'parking', label: 'Parking' },
  { value: 'breakfast', label: 'Petit-déjeuner' },
  { value: 'pool', label: 'Piscine' },
  { value: 'spa', label: 'Spa' },
  { value: 'gym', label: 'Salle de sport' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'room_service', label: 'Service en chambre' },
  { value: 'airport_shuttle', label: 'Navette aéroport' }
];

export interface Package {
  id: number;
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: 'B2B' | 'B2C';
  departure: string;
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  formulas: {
    id: number;
    name: string;
    priceType: string;
    perPersonPrice: number | null;
    roomPrices: {
      single: number;
      double: number;
      triple: number;
      quadruple: number;
      quintuple: number;
    };
  }[];
}

export interface PackageAgency {
  id: number;
  name: string;
  logo?: string | null;
  theme?: string | null;
  rating: number;
  verified: boolean;
  facebook?: string | null;
  instagram?: string | null;
  tiktok?: string | null;
}

export interface PackageAirline {
  id: number;
  name: string;
  logo: string;
  code?: string | null;
}

export interface SimilarPackage {
  id: number;
  title: string;
  description: string;
  image: string;
  departure: string;
  category: string;
  type: 'B2B' | 'B2C';
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  formulas: Formula[];
  agency: PackageAgency;
  airline?: PackageAirline | null;
}

export interface PackageDetails {
  id: number;
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: 'B2B' | 'B2C';
  departure: string;
  airline?: PackageAirline | null;
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  highlights: string[];
  included: string[];
  excluded: string[];
  formulas: Formula[];
  itinerary: {
    id: number;
    dayNumber: number;
    title: string;
    description: string;
    activities: string[];
    meals: {
      breakfast: boolean;
      lunch: boolean;
      dinner: boolean;
    };
    accommodation: string;
  }[];
  agency: PackageAgency;
  similarPackages: SimilarPackage[];
}

export function getDisplayPrice(formula: Formula): number {
  if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
    return formula.perPersonPrice;
  }
  
  if (formula.roomPrices) {
    const prices = Object.values(formula.roomPrices).filter(price => price > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }

  return 0;
}

export function formatPrice(formula: Formula): string {
  const price = getDisplayPrice(formula);
  const formattedPrice = price.toLocaleString('fr-DZ');
  
  if (formula.priceType === 'PER_PERSON') {
    return `${formattedPrice} DA par personne`;
  } else {
    return `À partir de ${formattedPrice} DA par personne (selon type de chambre)`;
  }
}

export interface PackageFormula {
  id: number;
  name: string;
  price: string;
} 